import { forwardRef } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { IconButton } from './IconButton';
import { Input } from './Input';
import type { InputProps } from './Input';

export type DateInputProps = Omit<InputProps, 'type'> & { onClear?: () => void };
export type DateTimeInputProps = Omit<InputProps, 'type'> & { onClear?: () => void };

function ClearButton({ onClear, label }: { onClear: () => void; label: string }): JSX.Element {
  return (
    <IconButton
      onClick={onClear}
      aria-label={label}
      icon={<XMarkIcon className="h-3.5 w-3.5" />}
      size="sm"
      shape="circle"
      className="hover:bg-muted"
    />
  );
}

// Thin wrappers composing Input with a pinned `type` AND a pinned
// `color-scheme` — the color-scheme pin is the real reason these are
// primitives rather than raw `<Input type="date" />` at call sites. A native
// date/datetime-local input's calendar-icon glyph is painted by the browser
// using the element's ambient `color-scheme` (light or dark); without an
// explicit pin, a dark-mode page still gets the LIGHT glyph (Chrome/Safari
// default an unstyled element to `light` regardless of the page's own dark
// theme), rendering it almost invisible against a dark background. Pinning
// `[color-scheme:light] dark:[color-scheme:dark]` keeps the glyph visible in
// both themes without hand-drawing a custom icon.
export const DateInput = forwardRef<HTMLInputElement, DateInputProps>(function DateInput(
  { className = '', onClear, value, adornmentEnd, ...rest },
  ref,
) {
  return (
    <Input
      ref={ref}
      value={value}
      className={`[color-scheme:light] dark:[color-scheme:dark] ${className}`}
      adornmentEnd={onClear && value ? <ClearButton onClear={onClear} label="Clear date" /> : adornmentEnd}
      {...rest}
      type="date"
    />
  );
});

export const DateTimeInput = forwardRef<HTMLInputElement, DateTimeInputProps>(function DateTimeInput(
  { className = '', onClear, value, adornmentEnd, ...rest },
  ref,
) {
  return (
    <Input
      ref={ref}
      value={value}
      className={`[color-scheme:light] dark:[color-scheme:dark] ${className}`}
      adornmentEnd={onClear && value ? <ClearButton onClear={onClear} label="Clear date and time" /> : adornmentEnd}
      {...rest}
      type="datetime-local"
    />
  );
});
